<template>
  <div data-test="filterNavigation" class="transparent-border d-print-none">
    <div data-test="closeDropdown">
      <AppMobileMenu
        v-if="$vuetify.breakpoint.smAndDown"
        v-model="reactiveMenu"
        absolute
        width="width: 100vw;"
      >
        <div class="filter-container pa-4">
          <div class="px-3" style="padding-left: 16px">
            <div class="d-flex flex-row justify-space-between align-center">
              <div>
                <h3 v-if="mobileLabelKey" class="primary--text">
                  {{ $t(mobileLabelKey) }}
                </h3>
              </div>
              <div>
                <v-btn
                  icon
                  class="text-right pr-0"
                  @click="reactiveMenu = false"
                >
                  <h3 class="py-2 center-icon-text">
                    <v-icon style="font-size: 24px">$iconTimesCircle</v-icon>
                  </h3>
                </v-btn>
              </div>
            </div>
          </div>

          <div class="mobile-filter-inputs">
            <v-row no-gutters class="no-mb">
              <slot v-bind="scopedSlot" />
            </v-row>

            <v-row v-if="$scopedSlots.advanced" no-gutters>
              <div class="col-12 mb-2">
                <strong class="primary--text">
                  {{ $t("calendar:mobile.search.SpecifyMore.heading") }}
                </strong>
              </div>
              <div class="disable-descent-row">
                <slot name="advanced" v-bind="scopedSlot" />
              </div>
            </v-row>
          </div>

          <div class="sticky-footer">
            <v-row no-gutters>
              <v-col>
                <v-btn
                  color="white accent--text"
                  class="px-1"
                  block
                  height="40px"
                  tile
                  @click="resetFilters"
                >
                  <h3>
                    <v-icon class="mr-2" color="accent">$iconReset</v-icon>
                    {{
                      $t(
                        "common:button.ResetFilters",
                        "Reset filters",
                        "Reset filters button"
                      )
                    }}
                  </h3>
                </v-btn>
              </v-col>
              <v-col>
                <v-btn
                  color="accent white--text"
                  class="px-1"
                  block
                  height="40px"
                  tile
                  @click="reactiveMenu = false"
                >
                  <h3>
                    {{ $t("common:ApplyFilters.button") }}
                  </h3>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
      </AppMobileMenu>

      <div v-else>
        <v-sheet color="strong" class="pa-2 elevation-1">
          <v-row dense class="px-2">
            <slot v-bind="scopedSlot" />
            <v-col v-if="showResetPublic" class="px-1 mb-2 text-end">
              <v-btn text depressed @click="resetFilters">
                <h3 class="py-2 center-icon-text">
                  <v-icon class="mr-2" color="edit">$iconReset</v-icon>
                  {{
                    $t(
                      "common:button.ResetFilters",
                      "Reset filters",
                      "Reset filters button"
                    )
                  }}
                </h3>
              </v-btn>
            </v-col>
          </v-row>
          <div v-if="$scopedSlots.advanced">
            <div class="d-flex px-2">
              <v-divider class="ma-auto" />

              <button
                data-test="advanceFilters"
                class="py-2 px-5 primary--text bold d-flex btn-focus"
                style="cursor: pointer"
                :aria-label="$t('common:expandButton.alt')"
                @click="advanced = !advanced"
              >
                <v-icon class="primary--text mr-2">
                  {{ advanced ? "$iconCircleCaretUp" : "$iconCircleCaretDown" }}
                </v-icon>
                <template v-if="totalSelected">
                  <b>
                    {{ totalSelected }}
                    {{
                      $t(
                        "common:button.filters.ItemsSelected",
                        "filter(s) selected",
                        "Additional filters button in mobile view when atleast one filter is selected"
                      )
                    }}
                  </b>
                </template>
                <template v-else>
                  <b>
                    {{
                      $t(
                        "common:button.filters.NothingSelected",
                        "Additional filters",
                        "Additional filters button in mobile view when nothing is selected"
                      )
                    }}
                  </b>
                </template>
              </button>

              <v-divider class="ma-auto" />
            </div>
            <div>
              <v-slide-y-transition>
                <div v-show="reactiveAdvanced" class="px-2">
                  <v-row dense>
                    <v-col md="10">
                      <v-row dense>
                        <slot name="advanced" v-bind="scopedSlot" />
                      </v-row>
                    </v-col>

                    <v-col
                      v-if="!showResetPublic"
                      v-show="$vuetify.breakpoint.mdAndUp"
                      md="2"
                      class="mt-4 px-1"
                    >
                      <v-btn
                        text
                        depressed
                        :disabled="dissallowedReset"
                        @click="resetFilters"
                      >
                        <h3 class="py-2">
                          <v-icon class="mr-2" color="edit">$iconReset</v-icon>
                          {{
                            $t(
                              "common:button.ResetFilters",
                              "Reset filters",
                              "Reset filters button"
                            )
                          }}
                        </h3>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </v-slide-y-transition>
            </div>
          </div>
        </v-sheet>
      </div>
    </div>

    <div v-if="$vuetify.breakpoint.smAndDown" :class="mobilePadding">
      <v-sheet color="strong" class="pa-2 elevation-1">
        <div>
          <slot name="mobile" />
        </div>
        <v-divider v-if="$slots.mobile" class="mt-4 mb-2" />
        <div class="text-center px-4">
          <v-btn tile text depressed @click="reactiveMenu = !reactiveMenu">
            <template v-if="totalSelected">
              <b class style="font-size: 18px">
                {{ totalSelected }}
                {{
                  $t(
                    "common:button.filters.ItemsSelected",
                    "filter(s) selected",
                    "Additional filters button in mobile view when atleast one filter is selected"
                  )
                }}
              </b>
            </template>
            <template v-else>
              <b class style="font-size: 18px">
                {{
                  $t(
                    "common:button.filters.NothingSelected",
                    "Additional filters",
                    "Additional filters button in mobile view when nothing is selected"
                  )
                }}
              </b>
            </template>
            <v-icon color="primary" class="ml-2">$iconCircleCaretRight</v-icon>
          </v-btn>
        </div>
      </v-sheet>
    </div>
  </div>
</template>
<script>
import { defineComponent, shallowReactive } from "vue";

import AppMobileMenu from "@/components/elements/AppMobileMenu.vue";
import { cloneDeep } from "lodash-es";

const observableState = shallowReactive({
  showCheckboxesView: false
});

export default defineComponent({
  name: "AppFilterNavigation",
  components: { AppMobileMenu },
  provide() {
    return {
      showCheckboxesView: () => observableState.showCheckboxesView
    };
  },
  props: {
    filters: {
      type: Object,
      default() {
        return {};
      }
    },
    filterName: {
      type: String,
      default: null
    },
    ignoreUrl: {
      type: Boolean,
      default: false
    },
    noPreserve: {
      type: Boolean,
      default: false
    },
    showResetPublic: {
      type: Boolean,
      default: false
    },
    ignoreList: {
      type: Array,
      default: () => [
        "keyword",
        "search",
        "eventsUntil",
        "createdAtUntil",
        "competitionStartDateUntil",
        "dateUntil"
      ]
    },
    mobileLabelKey: {
      type: String,
      default: null
    },
    continueAfterEmittedReset: {
      type: Boolean,
      default: false
    },
    fetchParams: {
      type: Boolean,
      default: false
    },
    useLocalStorage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menu: false,
      advanced: false,
      defaultFilterStore: null
    };
  },
  computed: {
    defaultFilters: {
      get() {
        return !this.defaultFilterStore
          ? null
          : JSON.parse(this.defaultFilterStore);
      },
      set(v) {
        this.defaultFilterStore = JSON.stringify(v);
      }
    },
    mobilePadding: ({ $vuetify }) => {
      if ($vuetify.breakpoint.xsOnly) {
        return "pa-1";
      } else if ($vuetify.breakpoint.smAndDown) {
        return "px-6 pb-8";
      } else return "pa-2";
    },
    scopedSlot: () => ({
      mobile: false,
      showCheckboxes: observableState.showCheckboxesView
    }),
    reactiveMenu: {
      get() {
        return this.$vuetify.breakpoint.mdAndUp || this.menu;
      },
      set(val) {
        const beacon = document.querySelector("#beacon-container");
        if (beacon) {
          beacon.style.display =
            val && !this.$vuetify.breakpoint.mdAndUp ? "none" : "initial";
        }
        this.menu = val;
      }
    },
    reactiveAdvanced() {
      if (this.$vuetify.breakpoint.mdAndUp) return this.advanced;
      return true;
    },
    // Watching this.filters will have new and old be the same
    // Hence for this computed being needed to the real new and old objects
    watcherFilters() {
      if (this.noPreserve) return null;
      return cloneDeep(this.filters);
    },
    totalSelected() {
      return Object.entries(this.filters).reduce((totals, [filterKey, val]) => {
        if (
          this.ignoreList.includes(filterKey) ||
          [null, undefined, ""].includes(val)
        ) {
          return totals;
        }

        const defaultValue = this.defaultFilters[filterKey] ?? null;

        if (
          (Array.isArray(val) && val.length) ||
          JSON.stringify(val) != JSON.stringify(defaultValue)
        ) {
          totals++;
        }

        return totals;
      }, 0);
    },
    dissallowedReset: ({ defaultFilterStore, filters }) =>
      defaultFilterStore === JSON.stringify(filters),
    sessionOrLocal() {
      if (this.useLocalStorage) return localStorage;
      return sessionStorage;
    }
  },
  watch: {
    "$vuetify.breakpoint.mdAndUp"(newVal) {
      observableState.showCheckboxesView = !newVal;
      this.reactiveMenu = newVal === true;
    },
    watcherFilters: {
      deep: true,
      handler(newVal, oldVal) {
        if (this.noPreserve) return;
        newVal = JSON.stringify(newVal);
        // If same as defaults, ignore
        if (JSON.stringify(this.defaultFilters) === newVal) {
          this.sessionOrLocal.removeItem(this.filterName);

          if (!this.ignoreUrl)
            history.replaceState(window.state, "", this.$route.path);
          return;
        }

        if (newVal != JSON.stringify(oldVal)) {
          this.setSession();
        }
      }
    }
  },
  created() {
    this.defaultFilters = JSON.parse(JSON.stringify(this.filters));
    if (this.fetchParams == true) this.findURLParams();
  },
  beforeMount() {
    observableState.showCheckboxesView = this.$vuetify.breakpoint.smAndDown;
  },
  methods: {
    findURLParams() {
      if (this.noPreserve || !this.filterName) return false;
      const clearout = () => {
        this.sessionOrLocal.removeItem(this.filterName);
        history.replaceState(window.state, "", this.$route.path);
      };

      try {
        // Fetch from URL
        if (!this.ignoreUrl && window.location.search.length > 1) {
          const params = new URLSearchParams(window.location.search);
          if (
            !params.has("filterName") ||
            params.get("filterName") != `"${this.filterName}"`
          ) {
            return false;
          }

          params.delete("filterName");

          const session = JSON.parse(this.defaultFilterStore);
          for (const [key, val] of params) {
            session[key] = JSON.parse(val);
          }

          if (this.defaultFilterStore === JSON.stringify(session)) {
            clearout();
            return false;
          }

          this.$emit("update:filters", {
            ...session
          });

          return true;
        }

        // Fetch from session/localStorage
        if (this.sessionOrLocal.getItem(this.filterName)) {
          const session = JSON.parse(
            this.sessionOrLocal.getItem(this.filterName)
          );

          if (this.defaultFilterStore === JSON.stringify(session)) {
            clearout();
            return false;
          }

          if (this.$dayjs().isBefore(session.expire)) {
            delete session.expire;
            delete session.filterName;

            this.$emit("update:filters", {
              ...this.defaultFilters,
              ...session
            });
            return true;
          }
          this.sessionOrLocal.removeItem(this.filterName);
        }

        return false;
      } catch (err) {
        this.$sentryThrowError.throwError(err, {
          level: "info",
          customTitle: `Filters couldn't be parse from JSON ${this.filterName}`
        });
      }
    },
    setSession() {
      if (!this.filterName || this.noPreserve) return false;

      const searchParams = new URLSearchParams(window.location.search);
      const filters = {};

      Object.entries(this.filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (
            value.length == 0 ||
            JSON.stringify(this.defaultFilters[key] ?? null) ==
              JSON.stringify(value ?? null)
          ) {
            searchParams.delete(key);
            return;
          }
        } else if (
          [null, undefined, "", this.defaultFilters[key]].includes(value)
        ) {
          searchParams.delete(key);
          return;
        }

        filters[key] = value;

        if (searchParams.has(key)) {
          searchParams.set(key, JSON.stringify(value));
        } else {
          searchParams.append(key, JSON.stringify(value));
        }
      });

      if (this.defaultFilterStore === JSON.stringify(filters)) {
        this.sessionOrLocal.removeItem(this.filterName);
        if (!this.ignoreUrl)
          history.replaceState(window.state, "", this.$route.path);
        return;
      }

      if (Object.keys(filters).length == 0) return;

      if (this.useLocalStorage) {
        filters.expire = this.$dayjs().add(10, "days");
        localStorage.setItem(this.filterName, JSON.stringify(filters));
      } else {
        // Set it to expire in 10 minutes in sessionStorage
        filters.expire = this.$dayjs().add(10, "minutes");
        sessionStorage.setItem(this.filterName, JSON.stringify(filters));
      }

      if (!this.ignoreUrl) {
        searchParams.set("filterName", JSON.stringify(this.filterName));
        history.replaceState(window.state, "", "?" + searchParams.toString());
      }
    },
    resetFilters() {
      if (this.$listeners["resetFilter"]) {
        this.$emit("resetFilter");
        if (!this.continueAfterEmittedReset) return;
      }

      this.$emit(
        "update:filters",
        JSON.parse(JSON.stringify(this.defaultFilters))
      );
      this.$nextTick(() => {
        if (!this.noPreserve)
          history.replaceState(window.state, "", this.$route.path);
      });
    }
  }
});

export const findURLParamsExternal = filterName => {
  const params = new URLSearchParams(window.location.search);
  if (
    params.has("filterName") &&
    params.get("filterName") == JSON.stringify(filterName)
  ) {
    params.delete("filterName");

    const session = {};
    for (const [key, val] of params) {
      try {
        session[key] = JSON.parse(val);
      } catch (err) {
        // This is more to do with storybook having own url params creating conflict
        //console.warn(err);
        // skip if cannot parse value
      }
    }

    return session;
  }

  function retrieveStorageValue(storage) {
    if (storage.getItem(filterName)) {
      const dayjs = require("dayjs");
      const session = JSON.parse(storage.getItem(filterName));
      if (dayjs().isBefore(session.expire)) {
        delete session.expire;
        delete session.filterName;
        return session;
      }
      storage.removeItem(filterName);
    }
    return undefined;
  }
  return (
    retrieveStorageValue(sessionStorage) || retrieveStorageValue(localStorage)
  );
};
</script>
<style lang="scss">
.filter-container {
  max-width: 620px;
  margin: auto;
}

.mobile-filter-inputs {
  $margin-bottom: 18px !important;
  padding: 0 8px;
  margin: auto;
  .searchbox-icon {
    margin-bottom: $margin-bottom;
  }

  .v-input {
    margin-bottom: $margin-bottom;
  }
  .no-mb > .mb-2 {
    margin-bottom: 0 !important;
  }
  .col-md-3,
  .col-12 {
    padding: 0;
    .v-input {
      margin-bottom: $margin-bottom;
    }
  }

  .disable-descent-row {
    width: 100%; // gives the whole width for inputs or else its small as heck
    & > .row {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
}
.sticky-footer {
  position: fixed;
  bottom: 10px;
  left: 0;
  width: 100%;
  z-index: 100;
}
</style>
